import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { supportApi } from '../api/support';
import { queryKeys } from '../lib/queryKeys';
import type { PaginationParams } from '../types/api';
import type { AdminTicketCreateRequest, AdminTicketUpdateRequest, AdminTicketReplyRequest, AdminEmailTemplateCreateRequest, AdminEmailTemplateUpdateRequest, AdminEmailPreviewRequest } from '../types/support';

export function useTicketsList(params: PaginationParams & { status?: string; priority?: string }) {
  return useQuery({ queryKey: queryKeys.support.tickets.list(params), queryFn: () => supportApi.listTickets(params) });
}

export function useTicketDetail(id: string) {
  return useQuery({ queryKey: queryKeys.support.tickets.detail(id), queryFn: () => supportApi.ticketDetail(id), enabled: !!id });
}

export function useCreateTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: AdminTicketCreateRequest) => supportApi.createTicket(data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: queryKeys.support.tickets.all }); message.success('Ticket created'); },
  });
}

export function useUpdateTicket(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: AdminTicketUpdateRequest) => supportApi.updateTicket(id, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: queryKeys.support.tickets.detail(id) }); qc.invalidateQueries({ queryKey: queryKeys.support.tickets.all }); message.success('Ticket updated'); },
  });
}

export function useReplyToTicket(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: AdminTicketReplyRequest) => supportApi.replyToTicket(id, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: queryKeys.support.tickets.detail(id) }); message.success('Reply sent'); },
  });
}

export function useTemplatesList() {
  return useQuery({ queryKey: queryKeys.support.templates.all, queryFn: () => supportApi.listTemplates() });
}

export function useTemplateDetail(id: string) {
  return useQuery({ queryKey: queryKeys.support.templates.detail(id), queryFn: () => supportApi.templateDetail(id), enabled: !!id });
}

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: AdminEmailTemplateCreateRequest) => supportApi.createTemplate(data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: queryKeys.support.templates.all }); message.success('Template created'); },
  });
}

export function useUpdateTemplate(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: AdminEmailTemplateUpdateRequest) => supportApi.updateTemplate(id, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: queryKeys.support.templates.detail(id) }); qc.invalidateQueries({ queryKey: queryKeys.support.templates.all }); message.success('Template updated'); },
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => supportApi.deleteTemplate(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: queryKeys.support.templates.all }); message.success('Template deleted'); },
  });
}

export function usePreviewTemplate(id: string) {
  return useMutation({
    mutationFn: (data: AdminEmailPreviewRequest) => supportApi.previewTemplate(id, data),
  });
}
